import React from 'react';
import { Form, Input, Select, Button } from 'antd';
import { OrderFile, WCCustomer } from '../../types/wordpress';

interface OrderFileFormProps {
    initialValues?: Partial<OrderFile>;
    customers: WCCustomer[];
    onSubmit: (values: Partial<OrderFile>) => Promise<void>;
    onCancel: () => void;
    loading?: boolean;
}

const OrderFileForm: React.FC<OrderFileFormProps> = ({
    initialValues,
    customers,
    onSubmit,
    onCancel,
    loading = false
}) => {
    const [form] = Form.useForm();

    const handleSubmit = async (values: any) => {
        const formData: Partial<OrderFile> = {
            title: {
                rendered: values.title
            },
            content: {
                rendered: values.content || ''
            },
            customer: values.customer_id ? {
                ID: parseInt(values.customer_id),
                display_name: customers.find(c => c.id.toString() === values.customer_id)?.first_name || ''
            } : undefined,
            status: initialValues?.id ? initialValues.status : 'draft'
        };

        await onSubmit(formData);
    };

    return (
        <Form
            form={form}
            onFinish={handleSubmit}
            initialValues={{
                title: initialValues?.title?.rendered || '',
                content: initialValues?.content?.rendered || '',
                customer_id: initialValues?.customer?.ID?.toString() || undefined
            }}
            layout="vertical"
            className="space-y-4"
        >
            <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true, message: 'Please enter a title' }]}
            >
                <Input className="w-full" />
            </Form.Item>

            <Form.Item
                name="content"
                label="Details"
            >
                <Input.TextArea rows={4} className="w-full" />
            </Form.Item>

            <Form.Item
                name="customer_id"
                label="Customer"
                rules={[{ required: true, message: 'Please select a customer' }]}
            >
                <Select
                    showSearch
                    placeholder="Select customer"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                        (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={customers.map(customer => ({
                        value: customer.id.toString(),
                        label: customer.first_name || `${customer.first_name}`.trim(),
                        disabled: customer.is_deleted
                    }))}
                    className="w-full"
                />
            </Form.Item>

            {initialValues?.order && (
                <Form.Item label="Linked Order" className="mb-4">
                    <Input
                        value={`Order #${initialValues.order}`}
                        disabled
                        className="bg-gray-50"
                    />
                </Form.Item>
            )}

            {initialValues?.status && (
                <Form.Item label="Current Status" className="mb-4">
                    <Input
                        value={initialValues.status.replace(/-/g, ' ').toUpperCase()}
                        disabled
                        className="bg-gray-50"
                    />
                </Form.Item>
            )}

            <div className="flex justify-end gap-2">
                <Button onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                    {initialValues?.id ? 'Update' : 'Create'}
                </Button>
            </div>
        </Form>
    );
};

export default OrderFileForm; 